import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSwitcher from '../components/common/LanguageSwitcher';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { Reveal, StaggerGroup, StaggerItem, Counter, TiltCard } from '../components/landing/motion';
import PhilippineFlagBackground from '../components/landing/PhilippineFlagBackground';
import { 
  Wrench, Droplets, Zap, Wind, Sparkles, Hammer, Tv, PaintBucket,
  ChevronRight, ChevronDown, Star, Shield, Clock, Phone, CheckCircle, Facebook, Twitter, Instagram, Mail, MapPin,
  Menu, X, Heart, Car, Home, Utensils, Baby, Dog, Cat, Laptop, Camera, Music, Book, Flower2, Scissors, Shirt,
  Dumbbell, Stethoscope, Briefcase, Truck, Package, Lightbulb, Thermometer, Wifi, Printer,
  Bug, Leaf, Monitor, Flame, Cog, Key, Lock, Paintbrush, Sofa, Fan, Refrigerator, WashingMachine,
  Drill, Ruler, Wrench as Tool2, CircuitBoard, Cpu, HardDrive as HardDriveIcon, Speaker, Headphones,
  GraduationCap, Building, Building2, Factory, Warehouse, Store, ShoppingBag, Gift, Gem, Crown,
  Umbrella, Sun, Moon, CloudRain, Snowflake, TreeDeciduous, Flower, Sprout, Apple, Carrot,
  Fish, Beef, Egg, Coffee, Wine, Beer, Pizza, IceCream, Cake, Cookie, Tag,
  Bed, Bath, Settings, Gauge, Smartphone, Watch, Glasses
} from 'lucide-react';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

// Extended icon map for all possible category icons
const iconMap = {
  // Original icons
  Droplets, Zap, Wind, Sparkles, Hammer, Tv, PaintBucket, Wrench,
  Shield, Clock, Phone, Star, CheckCircle,
  // Extended icons
  Heart, Car, Home, Utensils, Baby, Dog, Cat, Laptop, Camera, Music, Book, 
  Flower2, Scissors, Shirt, Dumbbell, Stethoscope, Briefcase, Truck, 
  Package, Lightbulb, Thermometer, Wifi, Printer,
  // New icons for missing categories
  Bug, Leaf, Monitor, Flame, Cog, Key, Lock, Paintbrush, Sofa, Fan,
  Drill, Ruler, Speaker, Headphones, GraduationCap, Building, Building2,
  Factory, Warehouse, Store, ShoppingBag, Gift, Gem, Crown, Umbrella,
  Sun, Moon, CloudRain, Snowflake, TreeDeciduous, Flower, Sprout, Apple,
  Carrot, Fish, Beef, Egg, Coffee, Wine, Beer, Pizza, IceCream, Cake, Cookie,
  // Additional icons
  Bed, Bath, Settings, Gauge, Smartphone, Watch, Glasses,
  // Alias for Tool -> Wrench
  Tool: Wrench,
  // Aliases - lowercase and common names
  'paint-bucket': PaintBucket,
  'paint': PaintBucket,
  'paintbucket': PaintBucket,
  'water': Droplets,
  'droplets': Droplets,
  'electric': Zap,
  'lightning': Zap,
  'zap': Zap,
  'ac': Wind,
  'aircon': Wind,
  'wind': Wind,
  'clean': Sparkles,
  'cleaning': Sparkles,
  'sparkles': Sparkles,
  'wood': Hammer,
  'carpenter': Hammer,
  'hammer': Hammer,
  'tv': Tv,
  'appliance': Tv,
  'wrench': Wrench,
  'handyman': Wrench,
  'health': Heart,
  'beauty': Heart,
  'heart': Heart,
  'car': Car,
  'auto': Car,
  'automotive': Car,
  'home': Home,
  'house': Home,
  'roofing': Home,
  'food': Utensils,
  'kitchen': Utensils,
  'utensils': Utensils,
  'baby': Baby,
  'child': Baby,
  'pet': Dog,
  'dog': Dog,
  'cat': Cat,
  'computer': Laptop,
  'tech': Laptop,
  'laptop': Laptop,
  'photo': Camera,
  'camera': Camera,
  'music': Music,
  'education': Book,
  'book': Book,
  'garden': Flower2,
  'flower2': Flower2,
  'salon': Scissors,
  'scissors': Scissors,
  'laundry': Shirt,
  'shirt': Shirt,
  'fitness': Dumbbell,
  'dumbbell': Dumbbell,
  'medical': Stethoscope,
  'stethoscope': Stethoscope,
  'delivery': Truck,
  'moving': Truck,
  'truck': Truck,
  'bug': Bug,
  'pest': Bug,
  'leaf': Leaf,
  'landscaping': Leaf,
  'monitor': Monitor,
  'flame': Flame,
  'heating': Flame,
  'fire': Flame,
  'cog': Cog,
  'settings': Settings,
  'key': Key,
  'locksmith': Key,
  'lock': Lock,
  'security': Shield,
  'shield': Shield,
  'paintbrush': Paintbrush,
  'painting': Paintbrush,
  'sofa': Sofa,
  'furniture': Sofa,
  'fan': Fan,
  'ventilation': Fan,
  'drill': Drill,
  'ruler': Ruler,
  'construction': Ruler,
  'speaker': Speaker,
  'audio': Speaker,
  'headphones': Headphones,
  'graduationcap': GraduationCap,
  'building': Building,
  'building2': Building2,
  'office': Building2,
  'warehouse': Warehouse,
  'store': Store,
  'shoppingbag': ShoppingBag,
  'shopping': ShoppingBag,
  'gift': Gift,
  'gem': Gem,
  'luxury': Gem,
  'crown': Crown,
  'vip': Crown,
  'umbrella': Umbrella,
  'sun': Sun,
  'solar': Sun,
  'cloudrain': CloudRain,
  'weather': CloudRain,
  'snowflake': Snowflake,
  'cooling': Snowflake,
  'treedeciduous': TreeDeciduous,
  'tree': TreeDeciduous,
  'sprout': Sprout,
  'plant': Sprout,
  'coffee': Coffee,
  'cafe': Coffee,
  'wine': Wine,
  'beverage': Wine,
  'pizza': Pizza,
  'bed': Bed,
  'bedroom': Bed,
  'bath': Bath,
  'bathroom': Bath,
  'thermometer': Thermometer,
  'temperature': Thermometer,
  'gauge': Gauge,
  'smartphone': Smartphone,
  'phone': Phone,
  'watch': Watch,
  'time': Watch,
  'glasses': Glasses,
  'optical': Glasses,
  'briefcase': Briefcase,
  'business': Briefcase,
  'package': Package,
};

// Color mapping based on icon name
const iconColorMap = {
  Droplets: 'bg-blue-100 text-blue-600',
  Zap: 'bg-yellow-100 text-yellow-600',
  Wind: 'bg-cyan-100 text-cyan-600',
  Sparkles: 'bg-pink-100 text-pink-600',
  Hammer: 'bg-amber-100 text-amber-600',
  Tv: 'bg-purple-100 text-purple-600',
  PaintBucket: 'bg-emerald-100 text-emerald-600',
  Wrench: 'bg-slate-100 text-slate-600',
  Heart: 'bg-rose-100 text-rose-600',
  Car: 'bg-indigo-100 text-indigo-600',
  Home: 'bg-teal-100 text-teal-600',
  Utensils: 'bg-orange-100 text-orange-600',
  Baby: 'bg-pink-100 text-pink-600',
  Dog: 'bg-amber-100 text-amber-600',
  Laptop: 'bg-blue-100 text-blue-600',
  Camera: 'bg-violet-100 text-violet-600',
  Music: 'bg-fuchsia-100 text-fuchsia-600',
  Book: 'bg-green-100 text-green-600',
  Flower2: 'bg-lime-100 text-lime-600',
  Scissors: 'bg-pink-100 text-pink-600',
  Shirt: 'bg-sky-100 text-sky-600',
  Dumbbell: 'bg-red-100 text-red-600',
  Stethoscope: 'bg-teal-100 text-teal-600',
  Truck: 'bg-gray-100 text-gray-600',
  // New icon colors
  Bug: 'bg-red-100 text-red-600',
  Leaf: 'bg-green-100 text-green-600',
  Monitor: 'bg-blue-100 text-blue-600',
  Flame: 'bg-orange-100 text-orange-600',
  Cog: 'bg-slate-100 text-slate-600',
  Key: 'bg-yellow-100 text-yellow-600',
  Lock: 'bg-slate-100 text-slate-600',
  Paintbrush: 'bg-purple-100 text-purple-600',
  Sofa: 'bg-amber-100 text-amber-600',
  Fan: 'bg-cyan-100 text-cyan-600',
  Drill: 'bg-slate-100 text-slate-600',
  Shield: 'bg-blue-100 text-blue-600',
  Package: 'bg-amber-100 text-amber-600',
  default: 'bg-slate-100 text-slate-600',
};

// Color name to Tailwind class mapping (matches admin panel colors)
const colorNameToClass = {
  // Blues
  blue: 'bg-blue-100 text-blue-600',
  sky: 'bg-sky-100 text-sky-600',
  cyan: 'bg-cyan-100 text-cyan-600',
  teal: 'bg-teal-100 text-teal-600',
  indigo: 'bg-indigo-100 text-indigo-600',
  
  // Greens
  emerald: 'bg-emerald-100 text-emerald-600',
  green: 'bg-green-100 text-green-600',
  lime: 'bg-lime-100 text-lime-600',
  
  // Warm colors
  yellow: 'bg-yellow-100 text-yellow-600',
  amber: 'bg-amber-100 text-amber-600',
  orange: 'bg-orange-100 text-orange-600',
  red: 'bg-red-100 text-red-600',
  rose: 'bg-rose-100 text-rose-600',
  
  // Pinks & Purples
  pink: 'bg-pink-100 text-pink-600',
  fuchsia: 'bg-fuchsia-100 text-fuchsia-600',
  purple: 'bg-purple-100 text-purple-600',
  violet: 'bg-violet-100 text-violet-600',
  
  // Neutrals
  slate: 'bg-slate-100 text-slate-600',
  gray: 'bg-gray-100 text-gray-600',
  stone: 'bg-stone-100 text-stone-600',
  zinc: 'bg-zinc-100 text-zinc-600',
};

// Default categories if CMS is empty
const defaultCategories = [
  { icon: 'Droplets', name: 'Plumbing', nameTl: 'Tubero', color: 'bg-blue-100 text-blue-600' },
  { icon: 'Zap', name: 'Electrical', nameTl: 'Elektrisyan', color: 'bg-yellow-100 text-yellow-600' },
  { icon: 'Wind', name: 'Aircon', nameTl: 'Aircon', color: 'bg-cyan-100 text-cyan-600' },
  { icon: 'Sparkles', name: 'Cleaning', nameTl: 'Paglilinis', color: 'bg-pink-100 text-pink-600' },
  { icon: 'Hammer', name: 'Carpentry', nameTl: 'Karpintero', color: 'bg-amber-100 text-amber-600' },
  { icon: 'Tv', name: 'Appliance', nameTl: 'Appliance', color: 'bg-purple-100 text-purple-600' },
  { icon: 'PaintBucket', name: 'Painting', nameTl: 'Pagpipinta', color: 'bg-emerald-100 text-emerald-600' },
  { icon: 'Wrench', name: 'Handyman', nameTl: 'Handyman', color: 'bg-slate-100 text-slate-600' },
];

function FAQItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-5 text-left hover:bg-stone-50 transition-colors"
        data-testid={`faq-toggle`}
      >
        <span className="font-medium text-slate-900 pr-4">{question}</span>
        <ChevronDown className={`w-5 h-5 text-slate-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-5 pb-5 text-slate-600 text-sm leading-relaxed border-t border-stone-100 pt-4">
          {answer}
        </div>
      )}
    </div>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [cmsContent, setCmsContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [promos, setPromos] = useState([]);
  const [adminBanners, setAdminBanners] = useState([]);

  useEffect(() => {
    fetchCMSContent();
    axios.get(`${API}/promo-codes/active`)
      .then(res => setPromos(res.data || []))
      .catch(() => {});
    // Fetch admin-managed banners for hero slider (same source as admin Banners page)
    axios.get(`${API}/banners`)
      .then(res => setAdminBanners(res.data || []))
      .catch(() => {});
  }, []);

  const fetchCMSContent = async () => {
    try {
      const response = await axios.get(`${API}/cms/landing`);
      setCmsContent(response.data);
      // Set document title from site identity
      if (response.data?.site_identity) {
        const si = response.data.site_identity;
        document.title = si.site_tagline ? `${si.site_title} - ${si.site_tagline}` : si.site_title;
      }
    } catch (error) {
      console.error('Failed to fetch CMS content:', error);
    } finally {
      setLoading(false);
    }
  };

  // Use CMS content or fallback to defaults
  const hero = cmsContent?.hero || {
    title: language === 'tl' ? 'Ang Iyong Bayani sa Tahanan' : "Your Home's Hero is Just a Tap Away",
    subtitle: language === 'tl' 
      ? 'Kumonekta sa verified at skilled na service providers para sa lahat ng pangangailangan sa iyong tahanan.'
      : 'Connect with verified, skilled service providers for all your home repair and maintenance needs.',
    cta_text: language === 'tl' ? 'Mag-book ng Bayani' : 'Book a Hero',
    cta_link: '/register'
  };

  // Hero slider images - from admin Banners only
  const heroSliderImages = adminBanners.length > 0
    ? adminBanners.map(b => ({ url: b.image_url, caption: b.title, link: b.link_url }))
    : [];
  
  // Hero slider state
  const [currentSlide, setCurrentSlide] = useState(0);
  
  // Auto-rotate slider
  useEffect(() => {
    if (heroSliderImages.length <= 1) return;
    setCurrentSlide(0);
    const interval = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % heroSliderImages.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [heroSliderImages.length, adminBanners.length]);

  const categories = cmsContent?.categories?.length > 0 
    ? cmsContent.categories 
    : defaultCategories;

  const testimonials = cmsContent?.testimonials?.length > 0 
    ? cmsContent.testimonials 
    : [
        { name: 'Maria Santos', location: 'Makati City', rating: 5, text: language === 'tl' ? 'Dumating ang tubero sa tamang oras at agad na naayos ang leak namin. Napaka-propesyonal!' : 'The plumber arrived on time and fixed our leak quickly. Very professional service!' },
        { name: 'Juan dela Cruz', location: 'Quezon City', rating: 5, text: language === 'tl' ? 'Pinadali ng HandyBayani ang paghahanap ng reliable na elektrisyan. Highly recommended!' : 'HandyBayani made finding a reliable electrician so easy. Highly recommended!' },
        { name: 'Ana Reyes', location: 'BGC, Taguig', rating: 5, text: language === 'tl' ? 'Great experience sa cleaning service. Napakaganda ng condo ko ngayon!' : 'Great experience with the cleaning service. My condo has never looked better!' },
      ];

  const faqs = cmsContent?.faqs?.length > 0 ? cmsContent.faqs : [];

  const footer = cmsContent?.footer || {
    company_name: 'HandyBayani',
    tagline: 'Your trusted home services partner',
    email: 'support@handybayani.com',
    phone: '+63 (2) 8123-4567',
    address: 'Manila, Philippines',
    social_links: { facebook: '#', twitter: '#', instagram: '#' },
    copyright_text: '© 2026 HandyBayani. All rights reserved.'
  };

  const stats = cmsContent?.stats || {
    total_bookings: 10000,
    total_providers: 500,
    average_rating: 4.9
  };

  const features = [
    { icon: Shield, title: language === 'tl' ? 'Verified na Providers' : 'Verified Providers', desc: language === 'tl' ? 'Lahat ng service providers ay background-checked at verified' : 'All service providers are background-checked and verified' },
    { icon: Clock, title: language === 'tl' ? 'Mabilis na Booking' : 'Quick Booking', desc: language === 'tl' ? 'Mag-book ng serbisyo sa loob ng 60 segundo' : 'Book a service in under 60 seconds with just a few taps' },
    { icon: Star, title: language === 'tl' ? 'Rated at Reviewed' : 'Rated & Reviewed', desc: language === 'tl' ? 'Pumili mula sa top-rated providers na may tunay na customer reviews' : 'Choose from top-rated providers with real customer reviews' },
    { icon: Phone, title: language === 'tl' ? 'Real-time Updates' : 'Real-time Updates', desc: language === 'tl' ? 'I-track ang iyong provider at makakuha ng live status updates' : 'Track your provider and get live status updates' },
  ];

  // Get icon component from string name
  const getIcon = (iconName) => {
    // Handle both direct icon names and aliases
    const normalizedName = iconName?.toLowerCase();
    return iconMap[iconName] || iconMap[normalizedName] || Wrench;
  };

  // Get color class for category - prioritizes backend icon_color field
  const getCategoryColor = (category) => {
    // First priority: check if category has icon_color from backend (e.g., "blue", "amber")
    if (category.icon_color && colorNameToClass[category.icon_color]) {
      return colorNameToClass[category.icon_color];
    }
    
    // Second priority: check if category has a direct color class set
    if (category.color) {
      return category.color;
    }
    
    // Third priority: fall back to icon-based mapping
    const iconName = category.icon;
    if (iconColorMap[iconName]) {
      return iconColorMap[iconName];
    }
    
    // Try to find the actual icon component for aliases
    const actualIcon = iconMap[iconName] || iconMap[iconName?.toLowerCase()];
    if (actualIcon) {
      // Find the key in iconColorMap that matches this component
      for (const [key, component] of Object.entries(iconMap)) {
        if (component === actualIcon && iconColorMap[key]) {
          return iconColorMap[key];
        }
      }
    }
    
    return iconColorMap.default;
  };

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-lg border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <a 
              href={cmsContent?.site_identity?.site_url || "https://handybayani.com"} 
              className="flex items-center gap-2 hover:opacity-80 transition-opacity"
            >
              {cmsContent?.site_identity?.site_logo ? (
                <img 
                  src={cmsContent.site_identity.site_logo.startsWith('http') 
                    ? cmsContent.site_identity.site_logo 
                    : `${BACKEND_URL}${cmsContent.site_identity.site_logo}`
                  } 
                  alt={cmsContent.site_identity?.site_title || 'HandyBayani'} 
                  className="h-10 w-auto object-contain"
                />
              ) : (
                <div className="w-10 h-10 bg-slate-900 rounded-xl flex items-center justify-center p-1.5">
                  <img src="/handybayani-logo.png" alt="HandyBayani" className="w-7 h-7 object-contain" />
                </div>
              )}
              <span className="text-xl font-bold text-slate-900 font-[Poppins]">{cmsContent?.site_identity?.site_title || t('app_name')}</span>
            </a>
            
            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-8">
              <a href="#services" className="text-slate-600 hover:text-slate-900 transition-colors">{t('nav_services')}</a>
              <a href="#how-it-works" className="text-slate-600 hover:text-slate-900 transition-colors">{t('landing_how_it_works')}</a>
              <a href="#reviews" className="text-slate-600 hover:text-slate-900 transition-colors">{language === 'tl' ? 'Mga Review' : 'Reviews'}</a>
              {faqs.length > 0 && (
                <a href="#faq" className="text-slate-600 hover:text-slate-900 transition-colors">FAQ</a>
              )}
            </nav>
            
            {/* Desktop Buttons */}
            <div className="hidden md:flex items-center gap-3">
              <LanguageSwitcher className="mr-2" />
              <Button 
                variant="ghost" 
                onClick={() => navigate('/login')}
                data-testid="desktop-login-btn"
              >
                {t('nav_login')}
              </Button>
              <Button 
                className="bg-slate-900 hover:bg-slate-800 text-white"
                onClick={() => navigate('/register')}
                data-testid="desktop-get-started-btn"
              >
                {t('landing_cta')}
              </Button>
            </div>
            
            {/* Mobile: Login, Get Started + Hamburger */}
            <div className="flex md:hidden items-center gap-1">
              <Button 
                variant="ghost" 
                size="sm"
                className="text-xs px-2"
                onClick={() => navigate('/login')}
                data-testid="mobile-login-btn"
              >
                {t('nav_login')}
              </Button>
              <Button 
                size="sm"
                className="bg-slate-900 hover:bg-slate-800 text-white text-xs px-3"
                onClick={() => navigate('/register')}
                data-testid="mobile-get-started-btn"
              >
                {t('landing_cta')}
              </Button>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 text-slate-600 hover:text-slate-900"
                data-testid="mobile-menu-btn"
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>
          
          {/* Mobile Menu Dropdown */}
          {mobileMenuOpen && (
            <div className="md:hidden border-t border-stone-200 py-4 bg-white">
              <nav className="flex flex-col gap-3">
                <a 
                  href="#services" 
                  className="text-slate-600 hover:text-slate-900 px-2 py-2 rounded-lg hover:bg-slate-100"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {t('nav_services')}
                </a>
                <a 
                  href="#how-it-works" 
                  className="text-slate-600 hover:text-slate-900 px-2 py-2 rounded-lg hover:bg-slate-100"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {t('landing_how_it_works')}
                </a>
                <a 
                  href="#reviews" 
                  className="text-slate-600 hover:text-slate-900 px-2 py-2 rounded-lg hover:bg-slate-100"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {language === 'tl' ? 'Mga Review' : 'Reviews'}
                </a>
                {faqs.length > 0 && (
                  <a 
                    href="#faq" 
                    className="text-slate-600 hover:text-slate-900 px-2 py-2 rounded-lg hover:bg-slate-100"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    FAQ
                  </a>
                )}
                <div className="px-2 pt-2 border-t border-stone-200">
                  <LanguageSwitcher />
                </div>
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" />
        {/* Interactive 4D waving Philippine flag */}
        <div className="absolute inset-0">
          <PhilippineFlagBackground className="absolute inset-0" />
        </div>
        {/* Readability scrim over the flag (left-weighted so text stays legible, flag stays vivid) */}
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/80 via-slate-950/35 to-transparent" />
        {hero.background_image && (
          <div 
            className="absolute inset-0 opacity-10 bg-cover bg-center"
            style={{ backgroundImage: `url(${hero.background_image.startsWith('http') || hero.background_image.startsWith('data:') ? hero.background_image : `${BACKEND_URL}${hero.background_image}`})` }}
          />
        )}
        <div className="absolute inset-0 opacity-10">
          <div className="hb-float absolute top-0 left-1/4 w-96 h-96 bg-amber-500 rounded-full blur-3xl" />
          <div className="hb-float-slow absolute bottom-0 right-1/4 w-96 h-96 bg-blue-500 rounded-full blur-3xl" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <StaggerGroup className="text-center lg:text-left">
              <StaggerItem>
                <div className="inline-flex items-center gap-2 bg-amber-500/20 text-amber-400 px-4 py-2 rounded-full text-sm font-medium mb-6">
                  <CheckCircle className="w-4 h-4" />
                  {language === 'tl' ? 'Pinagkakatiwalaan ng ' : 'Trusted by '}
                  <Counter value={stats.total_bookings || 0} suffix="+" className="font-bold text-amber-300" />
                  {language === 'tl' ? ' Filipino homes' : ' Filipino homes'}
                </div>
              </StaggerItem>

              <StaggerItem>
                <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.05] mb-6">
                  {hero.title?.includes('Bayani') ? (
                    <span dangerouslySetInnerHTML={{ __html: hero.title.replace('Bayani', '<span class="text-amber-500">Bayani</span>') }} />
                  ) : hero.title?.includes('Hero') ? (
                    <span dangerouslySetInnerHTML={{ __html: hero.title.replace('Hero', '<span class="text-amber-500">Hero</span>') }} />
                  ) : (
                    hero.title
                  )}
                </h1>
              </StaggerItem>

              <StaggerItem>
                <p className="text-lg text-slate-300 mb-8 max-w-xl mx-auto lg:mx-0">
                  {hero.subtitle}
                </p>
              </StaggerItem>

              <StaggerItem>
                <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                  <Button 
                    size="lg"
                    className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold h-14 px-8 text-lg hover:scale-[1.03] active:scale-95 transition-transform"
                    onClick={() => navigate(hero.cta_link || '/register')}
                    data-testid="hero-book-btn"
                  >
                    {hero.cta_text}
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </Button>
                  <Button 
                    size="lg"
                    variant="outline"
                    className="border-white/30 text-white hover:bg-white hover:text-slate-900 h-14 px-8 text-lg transition-colors"
                    onClick={() => navigate('/register?role=provider')}
                    data-testid="hero-provider-btn"
                  >
                    {language === 'tl' ? 'Maging Provider' : 'Become a Provider'}
                  </Button>
                </div>
              </StaggerItem>
            </StaggerGroup>
            
            {/* Hero Slider - Desktop */}
            {heroSliderImages.length > 0 && (
            <div className="relative hidden lg:block">
              <div className="relative w-full aspect-square max-w-lg mx-auto overflow-hidden rounded-3xl shadow-2xl">
                {heroSliderImages.map((img, index) => (
                  <img 
                    key={`slide-desktop-${index}`}
                    src={img.url?.startsWith('http') || img.url?.startsWith('data:') ? img.url : `${BACKEND_URL}${img.url}`}
                    alt={img.caption || `Slide ${index + 1}`}
                    className={`absolute inset-0 w-full h-full object-contain transition-opacity duration-700 ${index === currentSlide ? 'opacity-100' : 'opacity-0'}`}
                  />
                ))}
                {heroSliderImages.length > 1 && (
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                    {heroSliderImages.map((_, index) => (
                      <button
                        key={`dot-desktop-${index}`}
                        onClick={() => setCurrentSlide(index)}
                        className={`w-2 h-2 rounded-full transition-all ${index === currentSlide ? 'bg-white w-6' : 'bg-white/50'}`}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
            )}
          </div>
          
          {/* Hero Slider - Mobile */}
          {heroSliderImages.length > 0 && (
          <div className="lg:hidden mt-10">
            <div className="relative w-full aspect-square max-w-md mx-auto overflow-hidden rounded-2xl shadow-xl">
              {heroSliderImages.map((img, index) => (
                <img 
                  key={`slide-mobile-${index}`}
                  src={img.url?.startsWith('http') || img.url?.startsWith('data:') ? img.url : `${BACKEND_URL}${img.url}`}
                  alt={img.caption || `Slide ${index + 1}`}
                  className={`absolute inset-0 w-full h-full object-contain transition-opacity duration-700 ${index === currentSlide ? 'opacity-100' : 'opacity-0'}`}
                />
              ))}
              {heroSliderImages.length > 1 && (
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
                  {heroSliderImages.map((_, index) => (
                    <button
                      key={`dot-mobile-${index}`}
                      onClick={() => setCurrentSlide(index)}
                      className={`w-2 h-2 rounded-full transition-all ${index === currentSlide ? 'bg-white w-5' : 'bg-white/50'}`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
          )}
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-5xl font-extrabold text-slate-900 mb-4">
              {language === 'tl' ? 'Mga Serbisyo Namin' : 'Services We Offer'}
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              {language === 'tl' 
                ? 'Mula sa maliit na ayos hanggang sa malaking repair, ang aming verified professionals ay handang tumulong.'
                : 'From quick fixes to major repairs, our verified professionals are ready to help with all your home needs.'}
            </p>
          </Reveal>
          
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-6">
            {categories.map((cat, idx) => {
              const IconComponent = cat.icon ? (typeof cat.icon === 'string' ? getIcon(cat.icon) : cat.icon) : Wrench;
              const colorClass = getCategoryColor(cat);
              return (
                <Reveal key={idx} delay={(idx % 4) * 0.08} y={30}>
                  <TiltCard 
                    className="group h-full bg-white border border-stone-100 rounded-2xl p-6 shadow-[0_8px_32px_rgba(0,0,0,0.04)] hover:shadow-[0_20px_50px_rgba(0,0,0,0.10)] cursor-pointer"
                    onClick={() => navigate('/register')}
                    data-testid={`service-card-${(cat.name || '').toLowerCase()}`}
                  >
                    <div className={`w-14 h-14 ${colorClass} rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                      <IconComponent className="w-7 h-7" />
                    </div>
                    <h3 className="font-semibold text-slate-900 mb-1">{cat.name}</h3>
                    <p className="text-sm text-slate-500">
                      {cat.base_fee ? `Starting at ₱${cat.base_fee}` : 'Starting at ₱500'}
                    </p>
                  </TiltCard>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Promos Section */}
      {promos.length > 0 && (
        <section className="py-16 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 right-1/4 w-72 h-72 bg-amber-500 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-1/4 w-72 h-72 bg-blue-500 rounded-full blur-3xl" />
          </div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 bg-amber-500/20 text-amber-400 px-4 py-2 rounded-full text-sm font-medium mb-4">
                <Tag className="w-4 h-4" />
                {language === 'tl' ? 'Mga Espesyal na Alok' : 'Special Offers'}
              </div>
              <h2 className="font-display text-3xl md:text-5xl font-extrabold text-white mb-3">
                {language === 'tl' ? 'Mga Promo Para Sa Iyo' : 'Deals Just For You'}
              </h2>
              <p className="text-slate-400 max-w-xl mx-auto">
                {language === 'tl' 
                  ? 'Gamitin ang mga promo code na ito sa iyong susunod na booking at makatipid!'
                  : 'Use these promo codes on your next booking and save!'}
              </p>
            </div>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="landing-promos">
              {promos.map((promo, idx) => {
                const colors = [
                  'from-amber-500 to-orange-500',
                  'from-emerald-500 to-teal-500',
                  'from-violet-500 to-purple-500',
                  'from-rose-500 to-pink-500',
                  'from-sky-500 to-blue-500',
                ];
                const gradient = colors[idx % colors.length];
                
                return (
                  <Reveal key={promo.code} delay={(idx % 3) * 0.1} y={30}>
                  <div 
                    className={`bg-gradient-to-br ${gradient} rounded-2xl p-6 text-white relative overflow-hidden group hover:scale-[1.03] transition-transform h-full`}
                    data-testid={`promo-card-${promo.code}`}
                  >
                    <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2 group-hover:scale-125 transition-transform duration-500" />
                    <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2" />
                    
                    <div className="relative">
                      <p className="text-white/80 text-sm font-medium mb-1">
                        {promo.description || (language === 'tl' ? 'Espesyal na Alok' : 'Special Offer')}
                      </p>
                      <p className="text-3xl font-bold mb-3">
                        {promo.discount_type === 'percentage' 
                          ? `${promo.discount_value}% OFF`
                          : `₱${promo.discount_value} OFF`}
                      </p>
                      <div className="bg-white/20 backdrop-blur-sm rounded-lg px-3 py-2 inline-block mb-3">
                        <span className="font-mono font-bold text-sm tracking-wider">{promo.code}</span>
                      </div>
                      {promo.min_order_amount > 0 && (
                        <p className="text-white/70 text-xs">
                          {language === 'tl' 
                            ? `Min. order: ₱${promo.min_order_amount}`
                            : `Min. order: ₱${promo.min_order_amount}`}
                        </p>
                      )}
                    </div>
                  </div>
                  </Reveal>
                );
              })}
            </div>
            
            <div className="text-center mt-8">
              <Button 
                size="lg"
                className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold h-12 px-8"
                onClick={() => navigate('/register')}
                data-testid="promos-cta-btn"
              >
                {language === 'tl' ? 'Mag-register at Gamitin ang Promo' : 'Sign Up & Use a Promo'}
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Features Section */}
      <section id="how-it-works" className="py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-5xl font-extrabold text-slate-900 mb-4">
              {language === 'tl' ? 'Bakit Piliin ang HandyBayani?' : 'Why Choose HandyBayani?'}
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              {language === 'tl' 
                ? 'Committed kami na gawing simple, safe, at reliable ang home services para sa bawat Pilipinong pamilya.'
                : "We're committed to making home services simple, safe, and reliable for every Filipino family."}
            </p>
          </Reveal>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, idx) => (
              <Reveal key={idx} delay={idx * 0.1} className="text-center group">
                <div className="w-16 h-16 bg-slate-900 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:bg-amber-500 group-hover:-translate-y-1 transition-all duration-300">
                  <feature.icon className="w-8 h-8 text-amber-500 group-hover:text-slate-900 transition-colors" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-sm text-slate-600">{feature.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="reviews" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-5xl font-extrabold text-slate-900 mb-4">
              {language === 'tl' ? 'Ang Sabi ng Aming Mga Customer' : 'What Our Customers Say'}
            </h2>
          </Reveal>
          
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, tIdx) => (
              <Reveal key={testimonial.id || testimonial.name} delay={tIdx * 0.1}>
              <div className="bg-stone-50 rounded-2xl p-6 h-full border border-stone-100 hover:shadow-[0_20px_50px_rgba(0,0,0,0.08)] hover:-translate-y-1 transition-all duration-300">
                <div className="flex mb-4">
                  {[...Array(testimonial.rating || 5)].map((_, i) => (
                    <Star key={`star-${testimonial.name}-${i}`} className="w-5 h-5 text-amber-500 fill-amber-500" />
                  ))}
                </div>
                <p className="text-slate-700 mb-4">"{testimonial.text}"</p>
                <div className="flex items-center gap-3">
                  {testimonial.avatar_url ? (
                    <img src={testimonial.avatar_url} alt={testimonial.name} className="w-10 h-10 rounded-full object-cover" />
                  ) : (
                    <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center">
                      <span className="text-slate-600 font-semibold">{testimonial.name?.[0]}</span>
                    </div>
                  )}
                  <div>
                    <p className="font-medium text-slate-900">{testimonial.name}</p>
                    <p className="text-sm text-slate-500">{testimonial.location}</p>
                  </div>
                </div>
              </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      {faqs.length > 0 && (
        <section id="faq" className="py-20 bg-stone-50">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="font-display text-3xl md:text-5xl font-extrabold text-slate-900 mb-4">
                {language === 'tl' ? 'Mga Karaniwang Tanong' : 'Frequently Asked Questions'}
              </h2>
              <p className="text-slate-600">
                {language === 'tl' 
                  ? 'Narito ang mga sagot sa mga karaniwang katanungan tungkol sa HandyBayani.'
                  : 'Find answers to common questions about HandyBayani.'}
              </p>
            </div>
            
            <div className="space-y-3" data-testid="faq-section">
              {faqs.map((faq, idx) => (
                <FAQItem key={faq.id || idx} question={faq.question} answer={faq.answer} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 bg-slate-900 relative overflow-hidden grain-overlay">
        <div className="absolute inset-0 opacity-10">
          <div className="hb-float absolute top-0 left-1/3 w-72 h-72 bg-amber-500 rounded-full blur-3xl" />
        </div>
        <Reveal className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl md:text-5xl font-extrabold text-white mb-4">
            {language === 'tl' ? 'Handa Na Bang Ayusin ang Iyong Tahanan?' : 'Ready to Fix Your Home?'}
          </h2>
          <p className="text-slate-300 mb-8 text-lg">
            {language === 'tl' 
              ? 'Sumali sa libu-libong Filipino homeowners na nagtitiwala sa HandyBayani para sa kanilang home service needs.'
              : 'Join thousands of Filipino homeowners who trust HandyBayani for their home service needs.'}
          </p>
          <Button 
            size="lg"
            className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold h-14 px-10 text-lg hover:scale-[1.03] active:scale-95 transition-transform"
            onClick={() => navigate('/register')}
            data-testid="cta-book-btn"
          >
            {language === 'tl' ? 'Mag-book ng Unang Serbisyo' : 'Book Your First Service'}
            <ChevronRight className="w-5 h-5 ml-2" />
          </Button>
        </Reveal>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            {/* Brand */}
            <div className="md:col-span-2">
              <a 
                href={cmsContent?.site_identity?.site_url || "https://handybayani.com"} 
                className="flex items-center gap-2 mb-4 hover:opacity-80 transition-opacity"
              >
                {cmsContent?.site_identity?.site_logo ? (
                  <img 
                    src={cmsContent.site_identity.site_logo.startsWith('http') 
                      ? cmsContent.site_identity.site_logo 
                      : `${BACKEND_URL}${cmsContent.site_identity.site_logo}`
                    } 
                    alt={cmsContent.site_identity?.site_title || 'HandyBayani'} 
                    className="h-10 w-auto object-contain"
                  />
                ) : (
                  <div className="w-10 h-10 bg-slate-800 rounded-xl flex items-center justify-center p-1.5">
                    <img src="/handybayani-logo.png" alt="HandyBayani" className="w-7 h-7 object-contain" />
                  </div>
                )}
                <span className="text-xl font-bold text-white font-[Poppins]">{footer.company_name}</span>
              </a>
              <p className="text-slate-400 text-sm mb-4">{footer.tagline}</p>
              <div className="flex gap-4">
                {footer.social_links?.facebook && (
                  <a href={footer.social_links.facebook} className="text-slate-400 hover:text-white transition-colors">
                    <Facebook className="w-5 h-5" />
                  </a>
                )}
                {footer.social_links?.twitter && (
                  <a href={footer.social_links.twitter} className="text-slate-400 hover:text-white transition-colors">
                    <Twitter className="w-5 h-5" />
                  </a>
                )}
                {footer.social_links?.instagram && (
                  <a href={footer.social_links.instagram} className="text-slate-400 hover:text-white transition-colors">
                    <Instagram className="w-5 h-5" />
                  </a>
                )}
              </div>
            </div>
            
            {/* Contact */}
            <div>
              <h3 className="text-white font-semibold mb-4">{language === 'tl' ? 'Kontakin Kami' : 'Contact Us'}</h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-2 text-slate-400">
                  <Mail className="w-4 h-4" />
                  <span>{footer.email}</span>
                </div>
                <div className="flex items-center gap-2 text-slate-400">
                  <Phone className="w-4 h-4" />
                  <span>{footer.phone}</span>
                </div>
                <div className="flex items-center gap-2 text-slate-400">
                  <MapPin className="w-4 h-4" />
                  <span>{footer.address}</span>
                </div>
              </div>
            </div>
            
            {/* Quick Links */}
            <div>
              <h3 className="text-white font-semibold mb-4">{language === 'tl' ? 'Mga Link' : 'Quick Links'}</h3>
              <div className="space-y-2 text-sm">
                <a href="#services" className="block text-slate-400 hover:text-white transition-colors">
                  {language === 'tl' ? 'Mga Serbisyo' : 'Services'}
                </a>
                <a href="#how-it-works" className="block text-slate-400 hover:text-white transition-colors">
                  {language === 'tl' ? 'Paano Gumagana' : 'How It Works'}
                </a>
                <a href="#reviews" className="block text-slate-400 hover:text-white transition-colors">
                  {language === 'tl' ? 'Mga Review' : 'Reviews'}
                </a>
                <a href="/register?role=provider" className="block text-slate-400 hover:text-white transition-colors">
                  {language === 'tl' ? 'Maging Provider' : 'Become a Provider'}
                </a>
                {faqs.length > 0 && (
                  <a href="#faq" className="block text-slate-400 hover:text-white transition-colors">
                    FAQ
                  </a>
                )}
                <a href="/terms" className="block text-slate-400 hover:text-white transition-colors" data-testid="footer-terms-link">
                  {language === 'tl' ? 'Mga Tuntunin at Kundisyon' : 'Terms and Conditions'}
                </a>
                <a href="/privacy" className="block text-slate-400 hover:text-white transition-colors" data-testid="footer-privacy-link">
                  {language === 'tl' ? 'Patakaran sa Privacy' : 'Privacy Policy'}
                </a>
              </div>
            </div>
          </div>
          
          <div className="border-t border-slate-800 pt-8">
            <p className="text-slate-400 text-sm text-center">
              {footer.copyright_text}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
